import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import Field from 'redux-form/lib/Field'
import { connect } from 'react-redux'

import { formatCurrency } from '../service/i18n/format-currency.service'
import { Wallet } from './wallet/wallet.component'
import { CreditCard } from './credit-card/credit-card.component'
import { ConnectedTopupModal } from '../billing/topup-modal/topup-modal.component'
import {
  SelectPaymentMethodWrapper,
  StyledClassicRadioButton,
  RadioOptionGroup,
} from './payment-method-selection.component.styles'
import { Body } from '../component-library/design/typography/typography.styles'
import { getTotalCredit } from '../billing/duck/selectors'
import { PAYMENT_TYPE } from '../payment/payment.constants'

const PaymentMethodSelection = ({
  onPaymentMethodChange,
  options,
  orderQuotation,
  totalCredit,
  paymentType,
  hideDisclaimers,
  existingPaymentMeta,
}) => {
  const [t] = useTranslation()
  const [showTopupModal, setShowTopupModal] = useState(false)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(options[0])

  const isDisabled = !!existingPaymentMeta

  useEffect(() => {
    const selection = existingPaymentMeta?.method || paymentType || options[0]
    setSelectedPaymentMethod(selection)
    onPaymentMethodChange(selection)
  }, [existingPaymentMeta, onPaymentMethodChange, options, paymentType])

  const handleModalClose = () => {
    setShowTopupModal(false)
  }

  const handleModalOpen = () => {
    setShowTopupModal(true)
  }

  const _renderSinglePaymentMethod = paymentMethod => {
    switch (paymentMethod) {
      case 'credit_card': {
        return (
          <Field
            name="hasCreditCard"
            component={CreditCard}
            readOnlyCreditCardInfo={existingPaymentMeta?.meta?.credit_card}
          />
        )
      }
      case 'prepaid_wallet': {
        return (
          <Field
            component={Wallet}
            name="hasSufficientBalance"
            openModal={handleModalOpen}
            orderQuotation={orderQuotation}
            prepaidBalance={totalCredit}
            showBalance
            disabled={isDisabled}
          />
        )
      }
      case PAYMENT_TYPE.MONTHLY_SETTLEMENT: {
        return <Body>{t('orderdetail.paymentMethod.monthlySettlement')}</Body>
      }
      default:
        return null
    }
  }

  const _renderRadioGroup = () => {
    const handlePaymentSelection = event => {
      onPaymentMethodChange(event.target.value)
      setSelectedPaymentMethod(event.target.value)
    }

    const renderRadioOption = paymentMethod => {
      switch (paymentMethod) {
        case 'credit_card': {
          return (
            <React.Fragment>
              <StyledClassicRadioButton
                onChange={handlePaymentSelection}
                option={{
                  label: t('orderdetail.paymentMethod.creditCard'),
                  value: 'credit_card',
                  disabled: isDisabled,
                }}
                checked={selectedPaymentMethod === 'credit_card'}
              />
              {selectedPaymentMethod === 'credit_card' &&
                _renderSinglePaymentMethod('credit_card')}
            </React.Fragment>
          )
        }
        case 'prepaid_wallet': {
          return (
            <React.Fragment>
              <StyledClassicRadioButton
                onChange={handlePaymentSelection}
                option={{
                  label: `${t(
                    'orderdetail.paymentMethod.wallet'
                  )} (${formatCurrency({ amount: totalCredit })})`,
                  value: 'prepaid_wallet',
                  disabled: isDisabled,
                }}
                checked={selectedPaymentMethod === 'prepaid_wallet'}
                dataTestid="wallet-radio-option"
              />
              <Field
                component={Wallet}
                name="hasSufficientBalance"
                openModal={handleModalOpen}
                orderQuotation={orderQuotation}
                prepaidBalance={totalCredit}
                hideDisclaimers={hideDisclaimers}
                showMessage={selectedPaymentMethod === 'prepaid_wallet'}
                disabled={isDisabled}
              />
            </React.Fragment>
          )
        }
        default:
          return null
      }
    }

    const radioOptions = options.map(paymentMethod => (
      <RadioOptionGroup key={paymentMethod}>
        {renderRadioOption(paymentMethod)}
      </RadioOptionGroup>
    ))

    return (
      <SelectPaymentMethodWrapper>{radioOptions}</SelectPaymentMethodWrapper>
    )
  }

  const renderPaymentMethodSelection = () => {
    switch (options.length) {
      case 0:
        return null
      case 1:
        return _renderSinglePaymentMethod(options[0])
      default:
        return _renderRadioGroup()
    }
  }

  return (
    <React.Fragment>
      {renderPaymentMethodSelection()}
      {showTopupModal && (
        <ConnectedTopupModal handleModalClose={handleModalClose} />
      )}
    </React.Fragment>
  )
}

PaymentMethodSelection.propTypes = {
  onPaymentMethodChange: PropTypes.func,
  orderQuotation: PropTypes.number,
  options: PropTypes.arrayOf(PropTypes.string),
  totalCredit: PropTypes.number,
  paymentType: PropTypes.string,
  hideDisclaimers: PropTypes.bool,
  existingPaymentMeta: PropTypes.shape({
    method: PropTypes.string.isRequired,
    meta: PropTypes.shape({
      credit_card: PropTypes.shape({
        brand: PropTypes.string,
        last4: PropTypes.string,
      }),
    }),
  }),
}

const mapStateToProps = state => ({
  totalCredit: getTotalCredit(state),
})

const ConnectedPaymentMethodSelection = connect(mapStateToProps)(
  PaymentMethodSelection
)

export { ConnectedPaymentMethodSelection }
